import {
  FUEL_TYPES,
  type LinkableReport,
  OWNER_REFERENCE_KEYS,
  type OwnerReference,
  parseVoyageMaster,
  performanceReference,
  suggestReportLinks,
  type VoyageReportKind,
} from '@felix/domain';
import {
  ActionIcon,
  Button,
  Checkbox,
  Input,
  Modal,
  NumberInput,
  SegmentedControl,
  Select,
  Skeleton,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconBan,
  IconInfoCircle,
  IconLink,
  IconRefresh,
  IconShip,
  IconTrash,
  IconUsersGroup,
} from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTimeInput } from '../../inputs/DateTimeInput.js';
import { offsetChoices } from '../../inputs/lt-utc.js';
import { PortPicker } from '../../inputs/PortPicker.js';
import { menuLeaves } from '../../menu.js';
import { PageHeader } from '../../PageHeader.js';
import { StatusBadge } from '../../StatusBadge.js';
import { useShell } from '../../shell-context.js';
import { zoneOffsetMinutes } from '../personnel/rest-hours.js';
import { vesselTime } from '../personnel/ship-clock.js';
import { errorCode } from '../personnel/source.js';
import type { PersonnelVessel } from '../personnel/types.js';
import { CharterPartyFields } from './CharterPartyScreens.js';
import { CP_FORM_EMPTY, type CpFormValues, cpContentOf, cpFormErrors } from './charter-party.js';
import shared from './NoonReportScreen.module.css';
import {
  formatStamp,
  fromLocalInput,
  SectionTitle,
  toLocalInput,
  useNumber,
} from './NoonSections.js';
import { formatPlannedTime, formatTzOffset, TZ_OFFSETS } from './noon-report.js';
import {
  charterPartyOption,
  ownerReferenceInput,
  suggestedCharterParty,
  voyageReference,
} from './reference.js';
import type { VoyageSource } from './source.js';
import type { CharterPartyRegisterData, Voyage, VoyageInput, VoyageReport } from './types.js';
import { VoidDialog } from './VoidDialog.js';
import { shortLt } from './VoyageContextNotices.js';
import { VOYAGE_TONE } from './VoyageOverviewScreen.js';
import classes from './VoyageOverviewScreen.module.css';
import { CpTermsFacts, ReferenceSummary, useReferenceLabel } from './VoyageReference.js';
import reference from './VoyageReference.module.css';
import { assignedVoyageIdOf } from './voyage-record.js';
import { queueFrom } from './voyage-start.js';

type Values = Record<
  Exclude<
    keyof VoyageInput,
    'departureReportId' | 'linkReportIds' | 'charterPartyId' | 'ownerReference'
  >,
  string | number | null
>;

/**
 * Seferin CP'si (ADR 0084 Ek 2): CP yok, register'daki CP ya da (yalnız ofiste) aynı ekranda yeni
 * CP. Yeni CP gönderimde tek yazma yolundan CP Register'a yazılır; sefer yalnız kimliğini taşır.
 */
export type CpMode = 'none' | 'existing' | 'new';
type OwnerValues = Record<keyof OwnerReference, number | null>;
const OWNER_EMPTY: OwnerValues = {
  speedLadenKn: null,
  speedBallastKn: null,
  consumptionLadenMt: null,
  consumptionBallastMt: null,
};

/** Yeni CP'nin içeriği: yakıtlar seferin yakıt alanlarından (ortak bir kez sorar). */
export function newCpContent(values: CpFormValues, fuels: { main: unknown; aux: unknown }) {
  const fuel = (value: unknown) => (typeof value === 'string' && value ? value : null);
  return cpContentOf({ ...values, mainFuel: fuel(fuels.main), auxFuel: fuel(fuels.aux) });
}
type ValueKey = keyof Values;

const EMPTY: Values = {
  voyageNo: '',
  fromPort: '',
  toPort: '',
  fromPortCode: null,
  toPortCode: null,
  plannedDistanceNm: null,
  plannedEtd: '',
  plannedEtdTzOffsetMin: null,
  plannedEta: '',
  plannedEtaTzOffsetMin: null,
  charterer: '',
  charterPartyNo: '',
  cpSpeedLadenKn: null,
  cpSpeedBallastKn: null,
  cpConsumptionLadenMt: null,
  cpConsumptionBallastMt: null,
  mainEngineFuel: null,
  auxEngineFuel: null,
  cpMaxBeaufort: null,
  cpMaxWaveHeightM: null,
  remarks: '',
  notes: '',
};

function valuesOf(voyage: Voyage): Values {
  const out = { ...EMPTY };
  for (const key of Object.keys(EMPTY) as ValueKey[]) {
    out[key] = (voyage[key] as string | number | null) ?? EMPTY[key];
  }
  return out;
}

/** Sonraki sefer no önerisi: geminin son numarası bir artırılır ("V-2026-015" → "V-2026-016"). */
export function nextVoyageNo(existing: readonly string[], year: number): string {
  const numbers = existing
    .map((no) => /^V(?:Y)?-(\d{4})-(\d+)$/.exec(no))
    .filter((m): m is RegExpExecArray => m !== null && Number(m[1]) === year)
    .map((m) => Number(m[2]));
  const next = (numbers.length > 0 ? Math.max(...numbers) : 0) + 1;
  return `V-${year}-${String(next).padStart(3, '0')}`;
}

/** Formdaki numara: yazılan, boşsa öneri (öneri yer tutucudur; ADR 0047 §2). */
export function effectiveVoyageNo(typed: string, suggested: string): string {
  return typed.trim() || suggested;
}

/** Numara geminin başka bir seferinde var mı (sunucu da `DUPLICATE_VOYAGE_NO` ile reddeder). */
export function duplicateVoyageNo(
  no: string,
  voyages: readonly { id: string; voyageNo: string }[],
  selfId: string | null,
): boolean {
  const key = no.trim().toLowerCase();
  return voyages.some((v) => v.id !== selfId && v.voyageNo.trim().toLowerCase() === key);
}

/** Planlanan zamanın alanları (ADR 0054 §2). */
type PlannedKey = 'plannedEtd' | 'plannedEta';
const OFFSET_KEY = {
  plannedEtd: 'plannedEtdTzOffsetMin',
  plannedEta: 'plannedEtaTzOffsetMin',
} as const satisfies Record<PlannedKey, ValueKey>;

const text = (value: unknown) => (typeof value === 'string' && value.trim() ? value.trim() : null);

/**
 * "Start voyage" (ADR 0058 §3): seferin bilinenleri sefersiz kalkıştan: kalkış limanı ve Cast
 * Off (ETD), sonraki liman, ETA ve mesafe, kira referansı; saat farkı raporun farkı.
 */
export function valuesFromDeparture(departure: VoyageReport): Partial<Values> {
  const data = departure.data;
  const out: Partial<Values> = {
    fromPort: text(data.port) ?? '',
    fromPortCode: text(data.portCode),
    plannedEtd: departure.reportedAt,
    plannedEtdTzOffsetMin: departure.tzOffsetMin,
  };
  if (text(data.nextPort)) {
    out.toPort = text(data.nextPort);
    out.toPortCode = text(data.nextPortCode);
  }
  if (text(data.nextPortEta)) {
    out.plannedEta = text(data.nextPortEta);
    out.plannedEtaTzOffsetMin = departure.tzOffsetMin;
  }
  if (typeof data.distanceToNextPortNm === 'number') {
    out.plannedDistanceNm = data.distanceToNextPortNm;
  }
  // Kira referansı eski `charterPartyNo` alanına kopyalanmaz (ADR 0157 §7, #2274): bulut bu alanı
  // dönemi seferden alınan, şartsız bir "migrated" CP'ye çeviriyordu (ADR 0084 §5; S15'e aykırı).
  // Referans register'daki CP ile eşleştirilir (`matchingCharterParty`)
  return out;
}

/** Kalkışın kira referansı (Classification ▸ Charter / Voyage Reference). */
export function charterReferenceOf(departure: VoyageReport | null): string | null {
  const classification = (departure?.data.classification ?? {}) as Record<string, unknown>;
  return text(classification.charterReference);
}

/**
 * Referansla eşleşen kayıtlı CP (ADR 0157 §7): önce CP no, sonra kiracı; harf büyüklüğü fark
 * etmez, tam eşitlik. Dönemi kalkıştan (`at`) önce bitmiş CP eşleşmez (aynı kiracının eski
 * kirası; #2278); teslimi kalkıştan sonra olan (DLOSP) ya da dönemi henüz olmayan CP eşleşir.
 * Yoksa `null`.
 */
export function matchingCharterParty<
  T extends {
    cpNo: string | null;
    charterer: string | null;
    period?: { start: string | null; end: string | null };
  },
>(items: readonly T[], reference: string | null, at: string | null = null): T | null {
  const key = reference?.trim().toLowerCase();
  if (!key) return null;
  const when = at ? Date.parse(at) : Number.NaN;
  const live = items.filter((item) => {
    const end = item.period?.end;
    return Number.isNaN(when) || !end || Date.parse(end) > when;
  });
  const same = (value: string | null) => value?.trim().toLowerCase() === key;
  return live.find((item) => same(item.cpNo)) ?? live.find((item) => same(item.charterer)) ?? null;
}

/**
 * Seferle aynı yazımda bağlanacak raporlar (`linkReportIds`; ADR 0157 §4): tekil; seferin kalkışı
 * (`departureReportId`) ayrıca gönderildiği için listeden çıkar.
 */
export function linkInputIds(ids: readonly string[], departureReportId: string | null): string[] {
  return [...new Set(ids)].filter((id) => id !== departureReportId);
}

/**
 * Yeni seferle aynı yazımda bağlanacak sefersiz raporlar (ADR 0157 §4, #2267): `?departure`
 * verildiyse o kalkış ve ardından gelen sefersizler; yoksa ADR 0083 §3 kuralı (sefersiz kuyruğun ilk
 * kalkışından başlayıp başka sefere bağlı ilk rapora kadar). Tarih sırasıyla.
 */
export function reportsToLink(
  timeline: readonly VoyageReport[],
  voyages: readonly Voyage[],
  departureId: string | null,
): VoyageReport[] {
  if (departureId) return queueFrom(timeline, departureId, voyages);
  const linkable: LinkableReport[] = timeline.map((r) => ({
    id: r.id,
    kind: r.kind,
    reportedAt: r.reportedAt,
    voyageId: assignedVoyageIdOf(r, voyages),
    voided: r.status === 'void',
  }));
  // Yeni seferin kimliği yok: hiçbir raporun seferi olmayan yer tutucu (kuyruk kuralı)
  const ids = suggestReportLinks(linkable, '(yeni-sefer)');
  const byId = new Map(timeline.map((r) => [r.id, r]));
  return ids.map((id) => byId.get(id)).filter((r): r is VoyageReport => !!r);
}

/** Vazgeçte dönülecek yol (`?back=`): yalnız sefer ekranlarının iç yolu (ADR 0157 §1). */
export function safeBack(back: string | null | undefined): string | null {
  if (!back?.startsWith('/voyage/')) return null;
  // Yalnız var olan sefer sayfası: yönlendirici bilinmeyen yolu panoya atar (#2278)
  const path = back.split(/[?#]/)[0] ?? '';
  return VOYAGE_PATHS.has(path) ? back : null;
}

/** Sefer sayfalarının yolları (menüde görünmeyenler dahil; ADR 0082). */
const VOYAGE_PATHS = new Set(
  menuLeaves()
    .map(({ item }) => item.path)
    .filter((path) => path.startsWith('/voyage/')),
);

/**
 * Saat farkı değişince girilen yerel saat korunur, UTC an yeniden hesaplanır (kullanıcı limanın
 * saatini yazar, sonra farkı seçer).
 */
export function shiftOffset(iso: string | null, from: number, to: number): string | null {
  if (!iso) return null;
  return fromLocalInput(toLocalInput(iso, from), to);
}

export interface AddVoyageScreenProps {
  source: VoyageSource;
  /** Düzenlenecek sefer (`?voyage=<id>`); verilmezse yeni sefer. */
  voyageId?: string | null;
  /** Düzenlenecek seferin gemisi (`?vessel=<id>`): yalnız o geminin seferleri okunur. */
  vesselHint?: string | null;
  /**
   * Seferin başlayacağı sefersiz rapor (`?departure=<id>` ya da `?start=<id>`; ADR 0058 §3, ADR 0083
   * §2): sefer onunla açılır ve ona bağlanır.
   */
  departureId?: string | null;
  /**
   * Quick Create "Create New Voyage" (ADR 0083 §6): sefer açılınca bu türün formu yeni seferle
   * açılır (`?then=<tür>`). Verilmezse Voyage Details açılır; bağlanacak raporlar orada önerilir.
   */
  thenKind?: VoyageReportKind | null;
  now?: () => Date;
}

const systemNow = () => new Date();

/**
 * Yeni sefer (müşteri ekranı `ofis-yeni-sefer.jpeg`; ADR 0022 §1): sefer ofiste planlanır,
 * yalnız o geminin seferi gemiye iner. Gemide bu sayfa yalnız bilgi verir. Doğrulama sunucudaki
 * şemayla aynı (`voyageMasterSchema`).
 */
export function AddVoyageScreen({
  source,
  voyageId = null,
  vesselHint = null,
  departureId = null,
  thenKind = null,
  now = systemNow,
}: AddVoyageScreenProps) {
  const { t } = useTranslation();
  const shell = useShell();
  const n = useNumber();
  const planner = source.planner;
  const [vessels, setVessels] = useState<PersonnelVessel[] | null>(null);
  const [vesselId, setVesselId] = useState<string | null>(null);
  const [voyages, setVoyages] = useState<Voyage[]>([]);
  const [values, setValues] = useState<Values>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [departure, setDeparture] = useState<VoyageReport | null>(null);
  // `?departure` okunamadı: kayıt engellenir, sefer kalkışsız açılmaz (ADR 0157 §8)
  const [departureError, setDepartureError] = useState<string | null>(null);
  // Bağlanacak raporlar (ADR 0157 §4): adaylar ve seçim; `null` okunmadı
  const [candidates, setCandidates] = useState<VoyageReport[] | null>(null);
  const [linkSelected, setLinkSelected] = useState<Set<string>>(new Set());
  // Form öndeki kalkıştan dolduruldu (yalnız `?departure` yokken): seçim kalkınca geri alınır
  const [prefilledFrom, setPrefilledFrom] = useState<VoyageReport | null>(null);
  const [touched, setTouched] = useState(false);
  // Vazgeç: zincirden gelindiyse sefersiz kalkışa döner (ADR 0157 §1)
  const back = safeBack(new URLSearchParams(shell?.search ?? '').get('back'));
  // Seferin raporu var mı (ADR 0077 §4): yoksa silinir, varsa gerekçeyle iptal edilir
  const [reportCount, setReportCount] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<'confirm' | 'void' | null>(null);
  // Seferin CP'si ve Owner's reference'ı (ADR 0084 Ek 2)
  const [register, setRegister] = useState<CharterPartyRegisterData | null>(null);
  const [cpMode, setCpMode] = useState<CpMode>('none');
  const [cpId, setCpId] = useState<string | null>(null);
  const [cpTouched, setCpTouched] = useState(false);
  const [cpValues, setCpValues] = useState<CpFormValues>(CP_FORM_EMPTY);
  const [cpErrors, setCpErrors] = useState<Record<string, string>>({});
  const [owner, setOwner] = useState<OwnerValues>(OWNER_EMPTY);
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`voyage.planner.${key}`, options ?? {});
  const r = (key: string, options?: Record<string, unknown>) =>
    t(`voyage.cpReference.${key}`, options ?? {});
  const referenceLabel = useReferenceLabel();
  // Dar ekranda seçenekler alt alta: uzun adlar kesilmez, sayfa yana kaymaz
  const narrowModes = useMediaQuery('(max-width: 640px)') ?? false;

  useEffect(() => {
    source
      .vessels()
      .then((list) => {
        setVessels(list);
        setVesselId((current) => current ?? list[0]?.id ?? null);
      })
      .catch((error: unknown) =>
        setMessage({
          tone: 'danger',
          text: t(`voyage.errors.${errorCode(error)}`, {
            defaultValue: t('voyage.errors.GENERIC'),
          }),
        }),
      );
  }, [source, t]);

  // Düzenlemede seferin gemisi: bağlantıda verildiyse yalnız o gemi, yoksa sırayla aranır
  // (her `voyages` çağrısı geminin bütün seferlerinin durumunu türetir; Copilot, #63)
  useEffect(() => {
    if (!vessels || !voyageId) return;
    const hinted = vessels.find((v) => v.id === vesselHint);
    const order = hinted ? [hinted, ...vessels.filter((v) => v !== hinted)] : vessels;
    void (async () => {
      for (const vessel of order) {
        const list = await source.voyages(vessel.id);
        const found = list.find((v) => v.id === voyageId);
        if (found) {
          setVesselId(vessel.id);
          setVoyages(list);
          setValues(valuesOf(found));
          // Seferin CP'si ve Owner's reference'ı formda korunur (ADR 0084 Ek 2)
          setCpId(found.charterPartyId ?? null);
          setCpMode(found.charterPartyId ? 'existing' : 'none');
          setCpTouched(true);
          setOwner({ ...OWNER_EMPTY, ...(found.ownerReference ?? {}) });
          return;
        }
      }
    })();
  }, [vessels, voyageId, vesselHint, source]);

  // "Start voyage": sefersiz raporun bilinenleri forma (ADR 0058 §3, ADR 0083 §2)
  useEffect(() => {
    if (!vesselId || voyageId || !departureId) return;
    const unassigned = source.unassigned ?? source.departures;
    if (!unassigned) return;
    unassigned
      .call(source, vesselId)
      .then((list) => {
        const found = list.find((r) => r.id === departureId) ?? null;
        setDeparture(found);
        setDepartureError(found ? null : 'NOT_FOUND');
        if (found) setValues((current) => ({ ...current, ...valuesFromDeparture(found) }));
      })
      .catch((failure: unknown) => {
        setDeparture(null);
        setDepartureError(errorCode(failure));
      });
  }, [vesselId, voyageId, departureId, source]);

  // Bağlanacak raporlar (ADR 0157 §4): geminin zaman çizgisinden; eski kaynakta sefersizlerden
  useEffect(() => {
    if (!vesselId || voyageId) return;
    let live = true;
    void (async () => {
      try {
        const [timeline, list] = await Promise.all([
          source.allReports ? source.allReports(vesselId) : (source.unassigned?.(vesselId) ?? []),
          source.voyages(vesselId),
        ]);
        if (!live) return;
        const found = reportsToLink(timeline, list, departureId);
        setCandidates(found);
        setLinkSelected(new Set(found.map((r) => r.id)));
      } catch {
        if (live) setCandidates([]);
      }
    })();
    return () => {
      live = false;
    };
  }, [vesselId, voyageId, departureId, source]);

  // `?departure` yokken form öndeki seçili kalkıştan doldurulur (dokunulmamışsa); seçim kalkınca
  // doldurma geri alınır (ADR 0157 §4)
  const leadDeparture =
    !departureId && candidates?.[0]?.kind === 'departure' ? candidates[0] : null;
  const leadSelected = !!leadDeparture && linkSelected.has(leadDeparture.id);
  useEffect(() => {
    if (touched || departureId) return;
    if (leadDeparture && leadSelected && prefilledFrom?.id !== leadDeparture.id) {
      setValues((current) => ({ ...current, ...valuesFromDeparture(leadDeparture) }));
      setPrefilledFrom(leadDeparture);
    } else if (prefilledFrom && !leadSelected) {
      const keys = Object.keys(valuesFromDeparture(prefilledFrom)) as ValueKey[];
      setValues((current) => ({
        ...current,
        ...Object.fromEntries(keys.map((key) => [key, EMPTY[key]])),
      }));
      setPrefilledFrom(null);
    }
  }, [touched, departureId, leadDeparture, leadSelected, prefilledFrom]);

  useEffect(() => {
    if (!vesselId || voyageId) return;
    source
      .voyages(vesselId)
      // Numara alanı boş kalır; öneri yer tutucudur (elle yazılan numara; ADR 0047 §2)
      .then((list) => setVoyages(list))
      .catch(() => setVoyages([]));
  }, [vesselId, voyageId, source]);

  // Geminin CP register'ı ve Owner's reference varsayılanı (ADR 0084 Ek 2); eski kaynakta yok
  const loadRegister = useCallback(
    async (id: string) => {
      if (!source.charterParties) return null;
      try {
        const value = await source.charterParties(id);
        setRegister(value);
        return value;
      } catch {
        setRegister(null);
        return null;
      }
    },
    [source],
  );
  useEffect(() => {
    if (!vesselId) return;
    void loadRegister(vesselId);
  }, [vesselId, loadRegister]);

  // Yeni seferde planlanan kalkışı kapsayan CP önerilir; kullanıcı seçtikten sonra değişmez
  const plannedEtdValue = typeof values.plannedEtd === 'string' ? values.plannedEtd : '';
  // Kalkışın kira referansı (ADR 0157 §7): eski CP No alanına yazılmaz; eşleşen kayıtlı CP önerilir
  const charterRef = charterReferenceOf(departure ?? prefilledFrom);
  const charterAt = (departure ?? prefilledFrom)?.reportedAt ?? (plannedEtdValue || null);
  const matchedCp = matchingCharterParty(register?.items ?? [], charterRef, charterAt);
  useEffect(() => {
    if (voyageId || cpTouched || !register) return;
    const suggested =
      matchedCp ?? suggestedCharterParty(register.items, plannedEtdValue || null, now());
    setCpId(suggested?.id ?? null);
    setCpMode(suggested ? 'existing' : 'none');
  }, [voyageId, cpTouched, register, plannedEtdValue, now, matchedCp]);

  const editing = voyageId ? (voyages.find((v) => v.id === voyageId) ?? null) : null;
  const editingId = editing?.id ?? null;
  useEffect(() => {
    if (!editingId) return;
    source
      .reports(editingId)
      .then((list) => setReportCount(list.length))
      .catch(() => setReportCount(null));
  }, [editingId, source]);

  /** Sefer silme ya da iptali (ADR 0077 §4); bitince sefer listesine dönülür. */
  const withdraw = async (work: () => Promise<unknown>): Promise<string | null> => {
    setBusy(true);
    try {
      await work();
      setDeleting(null);
      shell?.navigate('/voyage/list');
      return null;
    } catch (failure) {
      const code = errorCode(failure);
      // Silmede ileti ekranda; iptalde iletişim kutusu açık kalır ve hatayı gösterir
      if (deleting === 'confirm') {
        setDeleting(null);
        setMessage({
          tone: 'danger',
          text: t(`voyage.errors.${code}`, { defaultValue: t('voyage.errors.GENERIC') }),
        });
      }
      return code;
    } finally {
      setBusy(false);
    }
  };
  const canWithdraw =
    !!editing && editing.status !== 'cancelled' && !!planner?.remove && reportCount !== null;
  const vessel = vessels?.find((v) => v.id === vesselId) ?? null;
  const set = (key: ValueKey) => (value: string | number | null) => {
    setTouched(true);
    setValues((current) => ({ ...current, [key]: value }));
  };
  // Seçili bağlanacak raporlar, tarih sırasıyla (ADR 0157 §4)
  const chosen = (candidates ?? []).filter((r) => linkSelected.has(r.id));
  const error = (key: string) => {
    const code = errors[key];
    return code ? t(`voyage.errors.${code}`, { defaultValue: t('voyage.errors.GENERIC') }) : null;
  };

  // Geminin saat farkı, gemi saati kaydından (ADR 0065 §4, §8): yeni seferin önerilen farkı
  const vesselZone = vessel ? zoneOffsetMinutes(vesselTime(vessel), now().getTime()) : 0;
  const offsetOf = (key: PlannedKey): number | null => {
    const stored = values[OFFSET_KEY[key]];
    if (typeof stored === 'number') return stored;
    return editing ? null : vesselZone;
  };

  const suggestedNo = nextVoyageNo(
    voyages.map((v) => v.voyageNo),
    now().getUTCFullYear(),
  );
  const voyageNo = effectiveVoyageNo(String(values.voyageNo ?? ''), editing ? '' : suggestedNo);
  const duplicate = voyageNo !== '' && duplicateVoyageNo(voyageNo, voyages, editing?.id ?? null);
  // Gövde her çizimde yeniden kurulur (ucuz); fark gemi saatine ve kayda bağlı
  const input = (() => {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(values)) {
      if (value === null || value === '') continue;
      out[key] = typeof value === 'string' ? value.trim() : value;
    }
    if (voyageNo) out.voyageNo = voyageNo;
    // Yeni seferde fark gemi saatinden önerilir; düzeltmede kayıtlı fark (eski seferde boş, UTC)
    for (const key of ['plannedEtd', 'plannedEta'] as const) {
      const offset = offsetOf(key);
      if (offset !== null && out[key]) out[OFFSET_KEY[key]] = offset;
      else delete out[OFFSET_KEY[key]];
    }
    // Kod her zaman gönderilir: metin değişip kod silinince eski kod kalmasın (ADR 0048 §3)
    out.fromPortCode = values.fromPortCode ?? null;
    out.toPortCode = values.toPortCode ?? null;
    // Planlanan CP (yalnız kimlik) ve seferin Owner's reference'ı (ADR 0084 Ek 2)
    out.charterPartyId = cpMode === 'existing' ? cpId : null;
    out.ownerReference = ownerReferenceInput(owner);
    return out;
  })();
  const editor = source.charterPartyEditor;
  const items = register?.items ?? [];
  const selectedCp = cpMode === 'existing' ? (items.find((i) => i.id === cpId) ?? null) : null;
  // Formun referansı: yeni CP'de girilen şartlar, değilse sunucunun kuralı (CP > owner > yok)
  const formReference =
    cpMode === 'new'
      ? performanceReference({
          charterParty: {
            id: null,
            cpNo: cpValues.cpNo || null,
            charterer: cpValues.charterer || null,
            terms: cpValues,
          },
          voyageOwner: ownerReferenceInput(owner),
          vesselOwner: register?.ownerReference ?? null,
        })
      : voyageReference(
          {
            id: editing?.id ?? '',
            charterPartyId: cpMode === 'existing' ? cpId : null,
            ownerReference: ownerReferenceInput(owner),
            start: editing?.start ?? null,
            plannedEtd: plannedEtdValue || null,
            ...(editing
              ? {
                  charterer: editing.charterer,
                  charterPartyNo: editing.charterPartyNo,
                  cpSpeedLadenKn: editing.cpSpeedLadenKn,
                  cpSpeedBallastKn: editing.cpSpeedBallastKn,
                  cpConsumptionLadenMt: editing.cpConsumptionLadenMt,
                  cpConsumptionBallastMt: editing.cpConsumptionBallastMt,
                }
              : {}),
          },
          // Seçilmemiş CP dönem kuralıyla bulunmasın: "CP yok" seçimi açık karardır
          cpMode === 'existing'
            ? register
            : { items: [], ownerReference: register?.ownerReference ?? null },
        ).reference;

  const submit = async () => {
    // Ofiste sefer talebi (ADR 0083 §5): gemi uygular
    if (!planner && !(source.requestChange && !voyageId)) return;
    if (!vesselId) return;
    setMessage(null);
    // `?departure` okunamadıysa sefer kalkışsız açılmaz (ADR 0157 §8)
    if (departureId && !departure && !editing) {
      setMessage({
        tone: 'danger',
        text: t('voyage.context.departureMissing', {
          error: t(`voyage.errors.${departureError ?? 'GENERIC'}`, {
            defaultValue: t('voyage.errors.GENERIC'),
          }),
        }),
      });
      return;
    }
    if (duplicate) {
      setErrors({ voyageNo: 'DUPLICATE_VOYAGE_NO' });
      setMessage({ tone: 'danger', text: t('voyage.errors.DUPLICATE_VOYAGE_NO') });
      return;
    }
    const parsed = parseVoyageMaster(input);
    // Ekran ETD'yi zorunlu tutar (şemada isteğe bağlı: eski seferler; ADR 0054 §2)
    const etdMissing = !input.plannedEtd;
    // Yeni CP aynı şemayla (`parseCharterParty`); kayıttan önce bütün form denetlenir
    const cpContent =
      cpMode === 'new'
        ? newCpContent(cpValues, { main: values.mainEngineFuel, aux: values.auxEngineFuel })
        : null;
    const cpIssues = cpContent ? cpFormErrors({ ...cpValues }) : {};
    setCpErrors(cpIssues);
    const cpMissing = cpMode === 'existing' && !cpId;
    if (cpMissing || Object.keys(cpIssues).length > 0) {
      if (cpMissing) setErrors({ charterPartyId: 'REQUIRED' });
      setMessage({ tone: 'danger', text: t('voyage.errors.INVALID_VOYAGE_REPORT') });
      return;
    }
    if (!parsed.ok || etdMissing) {
      setErrors({
        ...(etdMissing ? { plannedEtd: 'REQUIRED' } : {}),
        ...(parsed.ok ? {} : Object.fromEntries(parsed.issues.map((i) => [i.path, i.code]))),
      });
      setMessage({ tone: 'danger', text: t('voyage.errors.INVALID_VOYAGE_REPORT') });
      return;
    }
    setErrors({});
    setBusy(true);
    try {
      let voyage = parsed.value as VoyageInput;
      // Yeni CP önce CP Register'a (tek yazma yolu; ofis yazar), sefer kimliğini taşır
      if (cpContent && editor) {
        const saved = await editor.save(vesselId, cpContent);
        voyage = { ...voyage, charterPartyId: saved.id };
        setCpId(saved.id);
        setCpMode('existing');
        setCpValues(CP_FORM_EMPTY);
        void loadRegister(vesselId);
      }
      if (!planner) {
        await source.requestChange?.(vesselId, {
          action: 'create_voyage',
          voyage,
          reportIds: [
            ...new Set([...(departure ? [departure.id] : []), ...chosen.map((r) => r.id)]),
          ],
          note: null,
        });
        setMessage({ tone: 'success', text: k('requested') });
        shell?.navigate(`/voyage/reports?vessel=${vesselId}`);
        return;
      }
      if (editing) {
        await planner.update(editing.id, {
          rowVersion: editing.rowVersion,
          ...voyage,
        });
        setMessage({ tone: 'success', text: k('saved') });
      } else {
        // Sefer ve bağları tek yazımda (ADR 0157 §4; `linkReportIds` 1.0.3649'da da var)
        const linkIds = linkInputIds(
          chosen.map((r) => r.id),
          departure?.id ?? null,
        );
        const created = await planner.create(vesselId, {
          ...voyage,
          ...(departure ? { departureReportId: departure.id } : {}),
          ...(linkIds.length > 0 ? { linkReportIds: linkIds } : {}),
        });
        setMessage({ tone: 'success', text: k('created') });
        // Kalkışla başlayan sefer Leg / Event'te sürer (Next Report; ADR 0058 §3)
        if (departure || chosen[0]?.kind === 'departure') {
          shell?.navigate(
            departure?.kind === 'noon'
              ? `/voyage/noon-report?voyage=${created.id}`
              : `/voyage/legs?voyage=${created.id}`,
          );
          return;
        }
        // Quick Create: seçilen türün formu yeni seferle (ADR 0083 §6)
        if (thenKind) {
          shell?.navigate(
            thenKind === 'noon'
              ? `/voyage/noon-report?voyage=${created.id}`
              : `/voyage/legs?voyage=${created.id}&kind=${thenKind}`,
          );
          return;
        }
        // Sefer açıldı: uygun sefersiz raporlar Voyage Details'te bağlanmak üzere önerilir (sefer-5)
        shell?.navigate(`/voyage/details?voyage=${created.id}`);
        return;
      }
      shell?.navigate('/voyage/list');
    } catch (failure) {
      const code = errorCode(failure);
      setMessage({
        tone: 'danger',
        text: t(`voyage.errors.${code}`, { defaultValue: t('voyage.errors.GENERIC') }),
      });
    } finally {
      setBusy(false);
    }
  };

  const title = editing ? k('editTitle') : k('title');
  // Ofis yeni seferi talep olarak gönderir (ADR 0083 §5); seferi düzeltmek gemidedir
  const requesting = !planner && !!source.requestChange && !voyageId;
  if (!planner && !requesting) {
    return (
      <div className={classes.screen}>
        <PageHeader title={title} />
        <div className={shared.notice} data-tone="info">
          <IconInfoCircle size={16} aria-hidden />
          <span>{k('shipOnly')}</span>
        </div>
      </div>
    );
  }
  if (!vessels) return <Skeleton height={600} radius="md" />;

  const num = (key: ValueKey, label: string, unit?: string, decimals = 2) => (
    <NumberInput
      size="sm"
      label={label}
      withAsterisk={key === 'plannedDistanceNm'}
      hideControls
      decimalScale={decimals}
      value={typeof values[key] === 'number' ? (values[key] as number) : ''}
      onChange={(value) => set(key)(typeof value === 'number' ? value : null)}
      error={error(key)}
      {...(unit
        ? { rightSection: <span className={shared.unit}>{unit}</span>, rightSectionWidth: 56 }
        : {})}
      classNames={{ label: shared.label ?? '' }}
    />
  );
  // Eski sürümün sefere yazdığı CP değerleri (ADR 0084 §5): salt okunur, kayıtta değişmez
  const legacyCp = [
    values.charterer ? `${k('charterer')}: ${values.charterer}` : null,
    values.charterPartyNo ? `${k('charterPartyNo')}: ${values.charterPartyNo}` : null,
    typeof values.cpSpeedLadenKn === 'number'
      ? `${k('cpSpeedLaden')}: ${n(values.cpSpeedLadenKn)} kn`
      : null,
    typeof values.cpSpeedBallastKn === 'number'
      ? `${k('cpSpeedBallast')}: ${n(values.cpSpeedBallastKn)} kn`
      : null,
    typeof values.cpMaxBeaufort === 'number'
      ? `${k('beaufortMax')}: ${values.cpMaxBeaufort}`
      : null,
  ].filter((v): v is string => v !== null);
  const fuelOptions = FUEL_TYPES.map((value) => ({ value, label: value }));
  const timeOf = (key: PlannedKey) =>
    typeof values[key] === 'string' && values[key] ? (values[key] as string) : null;
  const plannedTime = (key: PlannedKey) => {
    const at = timeOf(key);
    const offset = offsetOf(key) ?? 0;
    const short = key === 'plannedEtd' ? 'ETD' : 'ETA';
    // Tek açık etiket iki kutunun üstünde (sahip: adlar açık yazılır, kesilmez; ADR 0054 §5)
    return (
      <Input.Wrapper
        label={k(key)}
        labelElement="div"
        withAsterisk
        classNames={{ label: shared.label ?? '' }}
      >
        <div className={classes.timePair}>
          {/* Limanın yerel saati + UTC farkı; saklama UTC (CLAUDE.md "Zaman"; G1-04 biçimi) */}
          <DateTimeInput
            size="sm"
            aria-label={k(key)}
            value={at ? toLocalInput(at, offset) : ''}
            onChange={(local) => set(key)(local ? fromLocalInput(local, offset) : null)}
            description={at ? `= ${formatStamp(at)} UTC` : k('localTimeHint')}
            inputWrapperOrder={['label', 'input', 'description', 'error']}
            error={error(key)}
            classNames={{ placeholder: shared.placeholder ?? '' }}
          />
          <Select
            size="sm"
            aria-label={t('voyage.planner.utcOffsetOf', { name: short })}
            data={offsetChoices(offset, TZ_OFFSETS).map((m) => ({
              value: String(m),
              label: formatTzOffset(m),
            }))}
            value={String(offset)}
            allowDeselect={false}
            onChange={(value) => {
              if (value === null) return;
              const next = Number(value);
              setValues((current) => ({
                ...current,
                [OFFSET_KEY[key]]: next,
                [key]: shiftOffset(timeOf(key), offset, next) ?? '',
              }));
            }}
            error={error(OFFSET_KEY[key])}
          />
        </div>
      </Input.Wrapper>
    );
  };

  return (
    <div className={classes.screen}>
      <PageHeader title={title} {...(editing ? { crumb: title } : {})} />
      <div className={classes.plannerGrid}>
        <div className={classes.center}>
          <section className={classes.headCard} aria-label={k('cardTitle')}>
            <div className={classes.vessel}>
              <span className={classes.vesselIcon} aria-hidden>
                <IconShip size={30} stroke={1.4} />
              </span>
              <div>
                <strong className={classes.vesselNameText}>
                  {editing ? editing.voyageNo : k('cardTitle')}
                </strong>
                <p className={shared.muted}>{k('cardDescription')}</p>
              </div>
            </div>
            <span />
            <div className={classes.statusBox}>
              <span className={shared.itemLabel}>{k('status')}</span>
              <StatusBadge tone={VOYAGE_TONE[editing?.status ?? 'planned']}>
                {t(
                  `voyage.voyageStatus.${editing?.voidedAt ? 'void' : (editing?.status ?? 'planned')}`,
                ).toUpperCase()}
              </StatusBadge>
            </div>
          </section>

          {requesting ? (
            <div className={shared.notice} data-tone="info">
              <IconInfoCircle size={16} aria-hidden />
              <span>{k('requestNotice')}</span>
            </div>
          ) : null}
          {departure ? (
            <div className={shared.notice} data-tone="info">
              <IconInfoCircle size={16} aria-hidden />
              <span>
                {k('fromDeparture', {
                  no: departure.reportNo ?? '',
                  port: text(departure.data.port) ?? '—',
                  at: `${formatStamp(departure.reportedAt)} UTC`,
                })}
              </span>
            </div>
          ) : null}
          {departureError && departureId && !editing ? (
            <div className={shared.notice} data-tone="danger" role="alert">
              <IconInfoCircle size={16} aria-hidden />
              <span>
                {t('voyage.context.departureMissing', {
                  error: t(`voyage.errors.${departureError}`, {
                    defaultValue: t('voyage.errors.GENERIC'),
                  }),
                })}
              </span>
            </div>
          ) : null}
          {!editing && candidates && candidates.length > 0 ? (
            <section className={shared.card} aria-label={t('voyage.context.reportsToLink.title')}>
              <SectionTitle
                icon={<IconLink size={18} />}
                title={t('voyage.context.reportsToLink.title')}
              />
              <p className={shared.muted}>
                {t(
                  planner
                    ? 'voyage.context.reportsToLink.description'
                    : 'voyage.context.reportsToLink.descriptionOffice',
                )}
              </p>
              <ul className={classes.linkList}>
                {candidates.map((r) => (
                  <li key={r.id}>
                    <Checkbox
                      checked={linkSelected.has(r.id)}
                      disabled={r.id === departure?.id}
                      onChange={() =>
                        setLinkSelected((current) => {
                          const next = new Set(current);
                          if (next.has(r.id)) next.delete(r.id);
                          else next.add(r.id);
                          return next;
                        })
                      }
                      label={[
                        r.reportNo,
                        t(`voyage.kinds.${r.kind}`),
                        `${shortLt(r.reportedAt, r.tzOffsetMin)} LT`,
                        text(r.data.port),
                        r.status === 'submitted' ? null : t(`voyage.event.status.${r.status}`),
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    />
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          <section className={shared.card} aria-label={`1. ${k('information')}`}>
            <SectionTitle icon={<IconShip size={18} />} title={`1. ${k('information')}`} />
            {/* Satırlar (ADR 0054 §1, §2): no | gemi; kalkış | varış limanı; ETD | ETA; mesafe | not */}
            <div className={classes.formGrid}>
              <TextInput
                size="sm"
                label={k('voyageNo')}
                withAsterisk
                value={String(values.voyageNo ?? '')}
                {...(editing ? {} : { placeholder: suggestedNo, description: k('voyageNoHint') })}
                onChange={(event) => set('voyageNo')(event.currentTarget.value)}
                error={
                  error('voyageNo') ?? (duplicate ? t('voyage.errors.DUPLICATE_VOYAGE_NO') : null)
                }
                // Açıklama kutunun altında: yan alanla hizalı (ADR 0054 §1)
                inputWrapperOrder={['label', 'input', 'description', 'error']}
                classNames={{ label: shared.label ?? '' }}
                rightSection={
                  <ActionIcon
                    variant="subtle"
                    aria-label={k('suggestNo')}
                    title={k('suggestNo')}
                    onClick={() => set('voyageNo')(suggestedNo)}
                  >
                    <IconRefresh size={16} />
                  </ActionIcon>
                }
              />
              <Select
                size="sm"
                label={k('vessel')}
                withAsterisk
                data={vessels.map((v) => ({
                  value: v.id,
                  label: v.imo ? `${v.name} (IMO: ${v.imo})` : v.name,
                }))}
                value={vesselId}
                allowDeselect={false}
                disabled={!!editing}
                onChange={(value) => {
                  if (!value) return;
                  setVesselId(value);
                  setValues((current) => ({ ...current, voyageNo: '' }));
                }}
                classNames={{ label: shared.label ?? '' }}
              />
              <PortPicker
                label={k('fromPort')}
                withAsterisk
                value={{
                  name: String(values.fromPort ?? ''),
                  code: typeof values.fromPortCode === 'string' ? values.fromPortCode : null,
                }}
                onChange={(port) =>
                  setValues((current) => ({
                    ...current,
                    fromPort: port.name,
                    fromPortCode: port.code,
                  }))
                }
                error={error('fromPort') ?? error('fromPortCode')}
                classNames={{ label: shared.label ?? '' }}
              />
              {/* Liman listesinden seçilir; listede yoksa metin kalır (ADR 0048 §6) */}
              <PortPicker
                label={k('toPort')}
                withAsterisk
                value={{
                  name: String(values.toPort ?? ''),
                  code: typeof values.toPortCode === 'string' ? values.toPortCode : null,
                }}
                onChange={(port) =>
                  setValues((current) => ({ ...current, toPort: port.name, toPortCode: port.code }))
                }
                error={error('toPort') ?? error('toPortCode')}
                classNames={{ label: shared.label ?? '' }}
              />
              {plannedTime('plannedEtd')}
              {plannedTime('plannedEta')}
              {num('plannedDistanceNm', k('plannedDistance'), 'NM', 0)}
              <Textarea
                size="sm"
                label={k('remarks')}
                placeholder={k('remarksPlaceholder')}
                maxLength={500}
                autosize
                minRows={3}
                value={String(values.remarks ?? '')}
                onChange={(event) => set('remarks')(event.currentTarget.value)}
                classNames={{ label: shared.label ?? '' }}
              />
            </div>
          </section>

          <section className={shared.card} aria-label={`2. ${k('charterParty')}`}>
            <SectionTitle icon={<IconUsersGroup size={18} />} title={`2. ${k('charterParty')}`} />
            <div className={classes.cpGrid}>
              {/*
               * Seferin CP'si (ADR 0084 Ek 2; ortak isteği 2026-10-20): register'dan seçilir ya da
               * ofiste aynı ekranda yeni CP açılır. Şartlar yalnız CP kaydındadır; sefer kimliği
               * taşır. CP yoksa analiz Owner's reference'ı kullanır. Eski sürümün sefere yazdığı
               * değerler salt okunur görünür ve kaydedilirken değişmeden gider.
               */}
              <div className={`${reference.wide} ${reference.modes}`}>
                <Input.Wrapper
                  label={r('mode')}
                  labelElement="div"
                  classNames={{ label: shared.label ?? '' }}
                >
                  <SegmentedControl
                    fullWidth
                    orientation={narrowModes ? 'vertical' : 'horizontal'}
                    aria-label={r('mode')}
                    value={cpMode}
                    onChange={(value) => {
                      setCpTouched(true);
                      setCpMode(value as CpMode);
                      setErrors((current) => {
                        const { charterPartyId: _cp, ...rest } = current;
                        return rest;
                      });
                    }}
                    data={(
                      ['none', 'existing', ...(requesting && editor ? ['new'] : [])] as const
                    ).map((value) => ({ value, label: r(`modes.${value}`) }))}
                  />
                </Input.Wrapper>
                {charterRef ? (
                  <div className={shared.notice} data-tone="info" role="status">
                    <IconInfoCircle size={16} aria-hidden />
                    <span>
                      {matchedCp
                        ? t('voyage.context.charter.matched', {
                            name: charterPartyOption(matchedCp),
                          })
                        : t('voyage.context.charter.noCp')}
                    </span>
                  </div>
                ) : null}
                {cpMode === 'existing' ? (
                  items.length === 0 ? (
                    <div className={shared.notice} data-tone="warning">
                      <IconInfoCircle size={16} aria-hidden />
                      <span>{r('noRegister')}</span>
                    </div>
                  ) : (
                    <>
                      <Select
                        size="sm"
                        label={r('select')}
                        placeholder={r('selectPlaceholder')}
                        withAsterisk
                        data={items.map((item) => ({
                          value: item.id,
                          label: `${charterPartyOption(item)} (${t(
                            `voyage.charterParties.status.${item.period.status}`,
                          )})`,
                        }))}
                        value={cpId}
                        onChange={(value) => {
                          setCpTouched(true);
                          setCpId(value);
                        }}
                        error={error('charterPartyId')}
                        classNames={{ label: shared.label ?? '' }}
                      />
                      {selectedCp ? <CpTermsFacts item={selectedCp} /> : null}
                    </>
                  )
                ) : null}
                {cpMode === 'new' ? (
                  <>
                    <div className={shared.notice} data-tone="info">
                      <IconInfoCircle size={16} aria-hidden />
                      <span>{r('newHint')}</span>
                    </div>
                    <CharterPartyFields
                      values={cpValues}
                      set={(key) => (value) =>
                        setCpValues((current) => ({ ...current, [key]: value }))
                      }
                      error={(key) =>
                        cpErrors[key]
                          ? t(`voyage.charterParties.errors.${cpErrors[key]}`, {
                              defaultValue: t('voyage.errors.GENERIC'),
                            })
                          : undefined
                      }
                      hireReports={register?.hireReports ?? []}
                      withFuel={false}
                    />
                  </>
                ) : null}
                {cpMode === 'none' ? (
                  <>
                    <ReferenceSummary reference={formReference} charterParty={null} basis={null} />
                    {requesting ? (
                      <fieldset className={shared.group}>
                        <legend className={shared.groupTitle}>{r('ownerVoyage')}</legend>
                        <div className={reference.ownerBody}>
                          <p className={shared.muted}>{r('ownerHint')}</p>
                          <div className={reference.ownerGrid}>
                            {OWNER_REFERENCE_KEYS.map((key) => {
                              const fallback = register?.ownerReference?.[key] ?? null;
                              const unit = key.startsWith('speed') ? 'kn' : 'MT/day';
                              return (
                                <NumberInput
                                  key={key}
                                  size="sm"
                                  label={r(key.replace(/Kn$|Mt$/, ''))}
                                  hideControls
                                  min={0}
                                  decimalScale={2}
                                  allowNegative={false}
                                  value={owner[key] ?? ''}
                                  placeholder={fallback !== null ? n(fallback, 2) : undefined}
                                  onChange={(value) =>
                                    setOwner((current) => ({
                                      ...current,
                                      [key]: typeof value === 'number' ? value : null,
                                    }))
                                  }
                                  error={error(`ownerReference.${key}`)}
                                  rightSection={<span className={shared.unit}>{unit}</span>}
                                  rightSectionWidth={64}
                                  classNames={{ label: shared.label ?? '' }}
                                />
                              );
                            })}
                          </div>
                        </div>
                      </fieldset>
                    ) : null}
                  </>
                ) : null}
                <div className={shared.notice} data-tone="info">
                  <IconInfoCircle size={16} aria-hidden />
                  <span>
                    {planner ? r('shipNote') : r('periodNote')}
                    {legacyCp.length > 0 ? (
                      <>
                        <br />
                        <small>{`${k('cpLegacy')} ${legacyCp.join(' · ')}`}</small>
                      </>
                    ) : null}
                    <br />
                    <Button
                      size="compact-sm"
                      variant="subtle"
                      px={0}
                      onClick={() =>
                        shell?.navigate(
                          source.scope === 'web' && vesselId
                            ? `/voyage/charter-parties?vessel=${vesselId}`
                            : '/voyage/charter-parties',
                        )
                      }
                    >
                      {k('cpRegisterOpen')}
                    </Button>
                  </span>
                </div>
              </div>
              <fieldset className={shared.group}>
                <legend className={shared.groupTitle}>{k('fuelType')}</legend>
                <div className={classes.twoCols}>
                  <Select
                    size="sm"
                    label={k('mainEngine')}
                    data={fuelOptions}
                    value={typeof values.mainEngineFuel === 'string' ? values.mainEngineFuel : null}
                    onChange={set('mainEngineFuel')}
                    clearable
                    classNames={{ label: shared.label ?? '' }}
                  />
                  <Select
                    size="sm"
                    label={k('auxEngine')}
                    data={fuelOptions}
                    value={typeof values.auxEngineFuel === 'string' ? values.auxEngineFuel : null}
                    onChange={set('auxEngineFuel')}
                    clearable
                    classNames={{ label: shared.label ?? '' }}
                  />
                </div>
              </fieldset>
              <Textarea
                size="sm"
                label={k('notes')}
                placeholder={k('notesPlaceholder')}
                maxLength={500}
                autosize
                minRows={3}
                value={String(values.notes ?? '')}
                onChange={(event) => set('notes')(event.currentTarget.value)}
                classNames={{ label: shared.label ?? '' }}
              />
            </div>
          </section>
        </div>

        <div className={classes.side}>
          <section className={shared.card} aria-label={k('summary')}>
            <SectionTitle icon={<IconShip size={18} />} title={k('summary')} />
            <dl className={classes.keyFigures}>
              {[
                [k('voyageNo'), voyageNo || '—'],
                [k('vessel'), vessel?.name ?? '—'],
                [t('voyage.overview.from'), String(values.fromPort || '—')],
                [t('voyage.overview.to'), String(values.toPort || '—')],
                [
                  k('plannedDistance'),
                  typeof values.plannedDistanceNm === 'number'
                    ? `${n(values.plannedDistanceNm, 0)} NM`
                    : '—',
                ],
                [
                  k('plannedEtd'),
                  formatPlannedTime(
                    timeOf('plannedEtd'),
                    timeOf('plannedEtd') ? offsetOf('plannedEtd') : null,
                  ),
                ],
                [
                  k('plannedEta'),
                  formatPlannedTime(
                    timeOf('plannedEta'),
                    timeOf('plannedEta') ? offsetOf('plannedEta') : null,
                  ),
                ],
                // Referans (ADR 0084 Ek 2): CP > Owner's reference > yok
                [r('reference'), referenceLabel(formReference)],
                [k('cpSpeedLaden'), n.unit(formReference.speedLadenKn, 'kn')],
                [k('cpSpeedBallast'), n.unit(formReference.speedBallastKn, 'kn')],
                [k('cpConsumptionLaden'), n.unit(formReference.consumptionLadenMt, 'MT/day', 1)],
                [
                  k('cpConsumptionBallast'),
                  n.unit(formReference.consumptionBallastMt, 'MT/day', 1),
                ],
                [k('fuelType'), `${values.mainEngineFuel ?? '—'} / ${values.auxEngineFuel ?? '—'}`],
              ].map(([label, value]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          </section>
          <div className={shared.notice} data-tone="info">
            <IconInfoCircle size={16} aria-hidden />
            <span>
              <strong>{k('note')}</strong>
              <br />
              {k('noteText')}
            </span>
          </div>
          {message ? (
            <p
              className={shared.message}
              data-tone={message.tone}
              role={message.tone === 'danger' ? 'alert' : 'status'}
            >
              {message.text}
            </p>
          ) : null}
          <div className={classes.formActions}>
            <Button
              size="md"
              variant="default"
              onClick={() => shell?.navigate(back ?? '/voyage/list')}
            >
              {k('cancel')}
            </Button>
            <Button size="md" loading={busy} onClick={() => void submit()}>
              {editing ? k('save') : requesting ? k('request') : k('create')}
            </Button>
          </div>
          {canWithdraw && editing ? (
            <div className={shared.notice} data-tone="danger">
              {reportCount === 0 ? (
                <IconTrash size={16} aria-hidden />
              ) : (
                <IconBan size={16} aria-hidden />
              )}
              <span>
                <strong>{t('voyage.withdraw.voyageTitle')}</strong>
                <br />
                {reportCount === 0
                  ? t('voyage.withdraw.voyageDeleteHint')
                  : t('voyage.withdraw.voyageVoidHint', { count: reportCount ?? 0 })}
                <br />
                <Button
                  size="xs"
                  mt={6}
                  variant="light"
                  color="red"
                  disabled={busy}
                  leftSection={reportCount === 0 ? <IconTrash size={14} /> : <IconBan size={14} />}
                  onClick={() => setDeleting(reportCount === 0 ? 'confirm' : 'void')}
                >
                  {reportCount === 0
                    ? t('voyage.withdraw.deleteVoyage')
                    : t('voyage.withdraw.voidVoyage')}
                </Button>
              </span>
            </div>
          ) : null}
          {editing?.voidedAt ? (
            <div className={shared.notice} data-tone="danger" role="status">
              <IconBan size={16} aria-hidden />
              <span>
                <strong>{t('voyage.withdraw.voyageVoidedTitle')}</strong>
                <br />
                {t('voyage.withdraw.voidedBecause', {
                  reason: editing.voidReason ?? '—',
                  at: formatStamp(editing.voidedAt),
                })}
              </span>
            </div>
          ) : null}
        </div>
      </div>
      {editing && planner?.remove ? (
        <Modal
          opened={deleting === 'confirm'}
          onClose={() => setDeleting(null)}
          title={t('voyage.withdraw.deleteVoyageTitle', { no: editing.voyageNo })}
          closeButtonProps={{ 'aria-label': k('cancel') }}
        >
          <p className={shared.muted}>{t('voyage.withdraw.deleteVoyageBody')}</p>
          <div className={shared.dialogActions}>
            <Button variant="default" onClick={() => setDeleting(null)}>
              {k('cancel')}
            </Button>
            <Button
              color="red"
              loading={busy}
              onClick={() =>
                void withdraw(
                  () => planner.remove?.(editing.id, editing.rowVersion) ?? Promise.resolve(),
                )
              }
            >
              {t('voyage.withdraw.deleteVoyage')}
            </Button>
          </div>
        </Modal>
      ) : null}
      {editing && planner?.voidVoyage ? (
        <VoidDialog
          opened={deleting === 'void'}
          subject="voyage"
          onClose={() => setDeleting(null)}
          onConfirm={(reason, password) =>
            withdraw(
              () =>
                planner.voidVoyage?.(editing.id, { reason, ...(password ? { password } : {}) }) ??
                Promise.resolve(),
            )
          }
        />
      ) : null}
    </div>
  );
}
